<template>
  <div v-if="isLoaded" role="main">
    <div v-if="pathwayIsCompleted" class="complete-pathway-page">
      <Confetti v-if="showConfetti" ref="confetti" />
      <h1
        data-test="cpp-title"
        class="col-md-12 complete-page-message text-center personal-pathway-message"
      >
        Congratulations! You have completed:
      </h1>

      <div v-if="!!outcome" class="col-md-12 outcome-title">
        <div class="outcome-title-text">
          <div class="outcome-title-header" v-html="cms.asHtml(outcome.full_title)"></div>
          <p class="outcome-title-desc" v-html="cms.asHtml(outcome.brief_description)"></p>
        </div>
        <div class="outcome-image-container">
          <img :src="outcome.image.url" :alt="cms.asText(outcome.name)" class="outcome-image" />
        </div>
      </div>

      <div style="margin-bottom: 20px" class="outcome-title complete-pathway-details">
        <div>
          <h2><b>Pathway: </b>{{ cms.asText(doc.data.title) }}</h2>
          <p v-html="cms.asHtml(doc.data.short_description)"></p>
        </div>
        <div class="outcome-image-container">
          <img
            :src="doc.data.title_image.url"
            :alt="cms.asText(outcome.name)"
            class="outcome-image"
          />
        </div>
      </div>

      <div class="complete-pathway-details">
        <p v-html="cms.asHtml(doc.data.pathway_complete_content)" class="pathway-complete-text"></p>
        <div class="pathway-benefits">
          <h4 class="benefits-title">Benefits</h4>
          <ul class="benefits-list">
            <li
              v-for="(benefit, index) in doc.data.benefits"
              :key="`benefit` + index"
              class="benefit-item"
            >
              <img src="../../assets/star-icon.svg" alt="bullet-point" class="star-icon" />
              <p>{{ cms.asText(benefit.benefit_description) }}</p>
            </li>
          </ul>
        </div>
      </div>
      <div class="complete-instructions">
        <!--      <p>{{ instructions }}</p>-->
      </div>
      <div class="button-container">
        <a class="btn btn-primary" href="/home?view=MyDashboard" data-test="cpp-complete-btn">
          Complete
        </a>
        <button class="btn btn-default-celebrate" @click="toggleConfetti" type="button">
          Celebrate!
        </button>
      </div>
    </div>

    <!-- NO PROGRESS CONTAINER -->
    <div v-else-if="!pathwayIsCompleted" class="no-progress-container">
      <div>
        <img
          src="../../assets/chameleon-magnifying-glass.png"
          alt="chameleon with a magnifying glass"
          data-test="cpp-error-image"
        />
      </div>
      <h3 class="text-center no-progress-message" data-test="cpp-error-text">
        "Oops! You need to complete every step before you can complete a pathway!"
      </h3>
    </div>
    <!-- END OF NO PROGRESS CONTAINER -->
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useAxios } from "../../composables/api";
import LoadingPage from "../loading-page.vue";
import Confetti from "../../components/lottie-confetti.vue";
import { PersonalPathwayProgressApiModel } from "../../api-models";
import cms from "../../services/cms/cms";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import { CmsGoalData, CmsPersonalPathway } from "../../services/cms/cms-type-definitions";

const doc = ref<CmsPersonalPathway | null>(null);
const hasNoCurrentPathway = ref(false);
const showConfetti = ref(false);

const { data: currentPathway, promise: currentPathwayPromise } =
  useAxios<PersonalPathwayProgressApiModel>("/api/personalpathway/current");

currentPathwayPromise.then((data) => {
  if (!data.currentPathway?.cmsPersonalPathwayId) {
    hasNoCurrentPathway.value = true;
    return;
  }
  cms
    .getById(CmsDocumentTypes.CmsPersonalPathway, data.currentPathway?.cmsPersonalPathwayId)
    .then((cmsData) => {
      doc.value = cmsData;
    });
});

const pathwayIsCompleted = computed(() => !!currentPathway.value?.currentPathway?.isCompleted);
const isLoaded = computed(
  () => (!!currentPathway.value && !!doc.value) || hasNoCurrentPathway.value,
);
const outcome = computed<CmsGoalData | undefined>(
  () => doc.value?.data.goal_relationship.data as CmsGoalData,
);

function toggleConfetti(): void {
  if (!showConfetti.value) {
    showConfetti.value = true;
    setTimeout(() => {
      showConfetti.value = false;
    }, 2000);
  }
}
</script>

<style scoped lang="less">
@import "../../styles/outcomes/complete-pathway.less";

.no-progress-container {
  max-width: 66%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
  margin-top: 20px;

  img {
    max-width: 400px;
  }
}
</style>

<!-- These styles are not scoped because they need to target elements produced by the CMS at runtime -->
<style lang="less">
@import "../../styles/outcomes/complete-pathway-cms-elements.less";
</style>
